import { Destination } from "Constant/destination";

import { getDestinationIconByCode } from "helpers/destinations/getDestinationIcon";

import { UNAVAILABLE_DESTINATION_TOOLTIP } from "./DestinationCreation.constants";

export const DestinationCategory = Object.freeze({
    META: "Meta",
    TIKTOK: "TikTok",
    GOOGLE: "Google",
    PINTEREST: "Pinterest",
    SNAP: "Snap",
    X: "X",
    REDDIT: "Reddit",
    LINKEDIN: "LinkedIn",
    MICROSOFT: "Microsoft",
    YANDEX: "Yandex",
    OPENAI: "OpenAI",
    OTHER: "Other",
});

/** Pill order in the catalog header. */
export const DESTINATION_CATEGORIES = [
    DestinationCategory.META,
    DestinationCategory.TIKTOK,
    DestinationCategory.GOOGLE,
    DestinationCategory.PINTEREST,
    DestinationCategory.SNAP,
    DestinationCategory.X,
    DestinationCategory.REDDIT,
    DestinationCategory.LINKEDIN,
    DestinationCategory.MICROSOFT,
    DestinationCategory.YANDEX,
    DestinationCategory.OPENAI,
    DestinationCategory.OTHER,
];

/** The API destination types carry no category / description; this fills them in. */
export const DESTINATION_CATALOG_META = Object.freeze({
    [Destination.fbpixel]: {
        category: DestinationCategory.META,
        description: "Send browser events to your Meta Pixel.",
    },
    [Destination.fbcapi]: {
        category: DestinationCategory.META,
        description:
            "Send web and app events server-side with the Meta Conversions API.",
    },
    [Destination.fboffline]: {
        category: DestinationCategory.META,
        description: "Upload offline conversions to Meta.",
    },
    [Destination.fbca]: {
        category: DestinationCategory.META,
        description: "Build and sync Meta Custom Audiences from your data.",
    },
    [Destination.ttpixel]: {
        category: DestinationCategory.TIKTOK,
        description: "Send browser events to your TikTok Pixel.",
    },
    [Destination.ttcapi]: {
        category: DestinationCategory.TIKTOK,
        description: "Send web events server-side with the TikTok Events API.",
    },
    [Destination.ttoffline]: {
        category: DestinationCategory.TIKTOK,
        description: "Upload offline events to a TikTok offline event set.",
    },
    [Destination.ttapp]: {
        category: DestinationCategory.TIKTOK,
        description: "Send in-app events to TikTok App Events.",
    },
    [Destination.ttca]: {
        category: DestinationCategory.TIKTOK,
        description: "Build and sync TikTok Custom Audiences from your data.",
    },
    [Destination.ttmessaging]: {
        category: DestinationCategory.TIKTOK,
        description: "Send messaging events to TikTok Messaging.",
    },
    [Destination.ggads]: {
        category: DestinationCategory.GOOGLE,
        description: "Upload offline conversions to Google Ads.",
    },
    [Destination.ggads_web]: {
        category: DestinationCategory.GOOGLE,
        description: "Send web conversions to Google Ads.",
    },
    [Destination.ggawc]: {
        category: DestinationCategory.GOOGLE,
        description: "Send website conversions to Google Ads.",
    },
    [Destination.ggaoc]: {
        category: DestinationCategory.GOOGLE,
        description: "Import offline conversions into Google Ads.",
    },
    [Destination.ggalc]: {
        category: DestinationCategory.GOOGLE,
        description: "Import lead conversions into Google Ads.",
    },
    [Destination.ggass]: {
        category: DestinationCategory.GOOGLE,
        description: "Upload store sales transactions to Google Ads.",
    },
    [Destination.ggads4]: {
        category: DestinationCategory.GOOGLE,
        description: "Send events to a Google Analytics 4 property.",
    },
    [Destination.ggbq]: {
        category: DestinationCategory.GOOGLE,
        description: "Stream your events into a Google BigQuery dataset.",
    },
    [Destination.pnpixel]: {
        category: DestinationCategory.PINTEREST,
        description: "Send browser events to your Pinterest Tag.",
    },
    [Destination.pncapi]: {
        category: DestinationCategory.PINTEREST,
        description:
            "Send events server-side with the Pinterest Conversions API.",
    },
    [Destination.scpixel]: {
        category: DestinationCategory.SNAP,
        description: "Send browser events to your Snap Pixel.",
    },
    [Destination.sccapi]: {
        category: DestinationCategory.SNAP,
        description: "Send events server-side with the Snap Conversions API.",
    },
    [Destination.twpixel]: {
        category: DestinationCategory.X,
        description: "Send browser events to your X Pixel.",
    },
    [Destination.twcapi]: {
        category: DestinationCategory.X,
        description: "Send events server-side with the X Conversion API.",
    },
    [Destination.rdpixel]: {
        category: DestinationCategory.REDDIT,
        description: "Send browser events to your Reddit Pixel.",
    },
    [Destination.rdcapi]: {
        category: DestinationCategory.REDDIT,
        description: "Send events server-side with the Reddit Conversions API.",
    },
    [Destination.licapi]: {
        category: DestinationCategory.LINKEDIN,
        description:
            "Send conversions server-side with the LinkedIn Conversions API.",
    },
    [Destination.msoffline]: {
        category: DestinationCategory.MICROSOFT,
        description: "Upload offline conversions to Microsoft Advertising.",
    },
    [Destination.ymwc]: {
        category: DestinationCategory.YANDEX,
        description: "Send web conversions to a Yandex Metrica counter.",
    },
    [Destination.oaipixel]: {
        category: DestinationCategory.OPENAI,
        description: "Send browser events to your OpenAI Ads pixel.",
    },
    [Destination.oaicapi]: {
        category: DestinationCategory.OPENAI,
        description:
            "Send events server-side with the OpenAI Ads Conversions API.",
    },
    [Destination.alpixel]: {
        category: DestinationCategory.OTHER,
        description: "Send browser events to your AppLovin pixel.",
    },
    [Destination.imcapi]: {
        category: DestinationCategory.OTHER,
        description: "Report conversions to Impact partnership tracking.",
    },
    [Destination.sstask]: {
        category: DestinationCategory.OTHER,
        description:
            "Run SignalSight tasks and automations when events arrive.",
    },
});

export const getDestinationCatalogMeta = (code, language) => {
    const meta = DESTINATION_CATALOG_META[code] ?? {};
    return {
        category: meta.category ?? DestinationCategory.OTHER,
        description:
            language?.destination_catalog?.[code] ?? meta.description ?? "",
    };
};

/**
 * Catalog items for `TypeCatalog` from the raw API destination types. Types
 * not in `availableCodes` are shown but disabled.
 */
export const buildDestinationCatalogItems = (
    destinationsTypes,
    availableCodes,
    language,
) =>
    (destinationsTypes ?? [])
        .filter((item) => item?.code)
        .map((item) => {
            const isAvailable =
                !availableCodes || availableCodes.includes(item.code);
            return {
                code: item.code,
                name: item.name ?? item.code,
                image: getDestinationIconByCode(item.code),
                testId: `create-destination-type-${item.code}`,
                disabled: !isAvailable,
                disabledLabel: isAvailable ? undefined : "Not available",
                disabledTooltip: isAvailable
                    ? undefined
                    : UNAVAILABLE_DESTINATION_TOOLTIP,
                ...getDestinationCatalogMeta(item.code, language),
            };
        });
